import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  Button,
  Table,
  Input,
  Row,
  Col,
  Card,
  Popconfirm,
  Dropdown,
  Menu,
  Tag,
  Typography,
  Switch,
  Drawer,
  Select,
  Space,
  Tooltip,
} from 'antd';
import { Link } from 'react-router-dom';
import { SearchOutlined, HolderOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import ParameterModal from './ParameterModal';
import AttributeService from '../../services/AttributeService';
import ToolService from '../../services/ToolService';
import { formatToTL, openNotification, PARAMETER_UNIT_OPTIONS, PARAMETER_PERIOD_OPTIONS } from '../../utils';
import DiscountCriteriaModal from './DiscountCriteriaModal';
import OfferingService from '../../services/OfferingService';
import { listGroupsAll } from '../../modules/groups/services/groupService';
import QuestionList from '../../modules/questions/views/List';
import AttributePreparationDrawer, { ATTRIBUTE_PREPARATION_UPDATED_EVENT } from './AttributePreparationDrawer';

import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DataType {
  id: string;
  name: string;
  description: string;
  price: number;
  toolId: number | null;
  orderNo: number | null;
  isActive: boolean;
  isGroup?: boolean;
  countable?: boolean;
  bulkReportEntry?: boolean;
  accreditationRequired?: boolean;
  accredited?: boolean;
  isDefault?: boolean;
  offering?: {
    id: number;
    name?: string;
  } | null;
  offeringId?: number | null;
  group?: {
    id: number;
    name?: string;
    isActive?: boolean;
  } | null;
  groupId?: number | null;
  unit?: string;
  period?: number;
  fieldControlTime?: number;
  reportPreparationTime?: number;
  children?: DataType[];
  key: string;
}

export type BooleanParameterFilter = 'all' | 'yes' | 'no';

export const getBooleanParameterLabel = (value?: boolean | null) =>
  value ? 'Evet' : 'Hayır';

export const matchesBooleanParameterFilter = (
  value: boolean | null | undefined,
  filter: BooleanParameterFilter,
) => {
  if (filter === 'all') {
    return true;
  }
  return filter === 'yes' ? value === true : value !== true;
};

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

interface FilterState {
  offeringId: number | null;
  groupId: number | null;
  countableFilter: 'all' | 'countable' | 'uncountable';
  accreditationRequiredFilter: BooleanParameterFilter;
  accreditedFilter: BooleanParameterFilter;
  unitFilter: string | null;
  showInactive: boolean;
  reportFormatsFilter: 'all' | 'with' | 'without';
  preparationTextFilter: 'all' | 'with' | 'without';
}

interface ParameterListProps {
  questionAttributeId?: number | null;
  onQuestionDrawerChange?: (visible: boolean, attributeId?: number) => void;
}

const PARAMETER_UNIT_LABELS = PARAMETER_UNIT_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const PARAMETER_PERIOD_LABELS = PARAMETER_PERIOD_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[String(option.value)] = option.label;
  return acc;
}, {});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <HolderOutlined
      ref={setActivatorNodeRef}
      {...listeners}
      style={{ cursor: 'move', color: '#999' }}
    />
  );
};

const ParameterList: React.FC<ParameterListProps> = ({ questionAttributeId, onQuestionDrawerChange }) => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<any>(null);
  const [data, setData] = useState<DataType[]>([]);
  const [selectedAttributeId, setSelectedAttributeId] = useState<number | null>(
    null
  );
  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [tools, setTools] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [offerings, setOfferings] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const createDefaultFilters = () => ({
    offeringId: null,
    groupId: null,
    countableFilter: 'all' as const,
    accreditationRequiredFilter: 'all' as BooleanParameterFilter,
    accreditedFilter: 'all' as BooleanParameterFilter,
    unitFilter: null,
    showInactive: false,
    reportFormatsFilter: 'all' as const,
    preparationTextFilter: 'all' as const,
  });
  const [filters, setFilters] = useState<FilterState>(createDefaultFilters);
  const [drawerFilters, setDrawerFilters] = useState<FilterState>(createDefaultFilters);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
  const [selectedAttributeForQuestions, setSelectedAttributeForQuestions] = useState<any | null>(null);
  const [preparationDrawerAttributeId, setPreparationDrawerAttributeId] = useState<number | null>(null);
  const [preparationDrawerKey, setPreparationDrawerKey] = useState(0);
  const { Title } = Typography;

  const handleOpenDiscountModal = (attributeId: number) => {
    setSelectedAttributeId(attributeId);
    setDiscountModalVisible(true);
  };

  const handleOpenQuestions = (attribute: any) => {
    if (!attribute || attribute.isGroup) {
      return;
    }
    setSelectedAttributeForQuestions(attribute);
    setIsQuestionDrawerOpen(true);
    onQuestionDrawerChange?.(true, attribute?.id);
  };

  const handleCloseQuestions = () => {
    setIsQuestionDrawerOpen(false);
    setSelectedAttributeForQuestions(null);
    onQuestionDrawerChange?.(false);
  };

  const getAllAttributes = useCallback(() => {
    AttributeService.getAll({
      reportFormatsFilter: filters.reportFormatsFilter,
      preparationTextFilter: filters.preparationTextFilter,
    })
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.error('Error fetching attributes:', error);
      });
  }, [filters.preparationTextFilter, filters.reportFormatsFilter]);

  useEffect(() => {
    getAllAttributes();
    getAllTools();
    getAllOfferings();
    getAllGroups();
  }, [getAllAttributes]);

  useEffect(() => {
    if (filterDrawerVisible) {
      setDrawerFilters({ ...filters });
    }
  }, [filterDrawerVisible, filters]);

  useEffect(() => {
    const handlePreparationUpdated = () => {
      getAllAttributes();
    };

    window.addEventListener(ATTRIBUTE_PREPARATION_UPDATED_EVENT, handlePreparationUpdated);
    return () => {
      window.removeEventListener(ATTRIBUTE_PREPARATION_UPDATED_EVENT, handlePreparationUpdated);
    };
  }, [getAllAttributes]);

  const findAttributeInData = useCallback(
    (targetId: number | undefined | null) => {
      if (targetId === undefined || targetId === null) {
        return null;
      }
      const numericId = Number(targetId);
      if (!Number.isFinite(numericId)) {
        return null;
      }
      const traverse = (items: any[]): any | null => {
        for (const item of items) {
          if (Number(item?.id) === numericId && !item?.isGroup) {
            return item;
          }
          if (Array.isArray(item?.children)) {
            const match = traverse(item.children);
            if (match) {
              return match;
            }
          }
        }
        return null;
      };
      return traverse(data);
    },
    [data],
  );

  useEffect(() => {
    if (questionAttributeId === undefined) {
      if (typeof onQuestionDrawerChange === 'function' && isQuestionDrawerOpen) {
        setIsQuestionDrawerOpen(false);
        setSelectedAttributeForQuestions(null);
      }
      return;
    }
    if (questionAttributeId === null) {
      if (isQuestionDrawerOpen) {
        setIsQuestionDrawerOpen(false);
        setSelectedAttributeForQuestions(null);
      }
      return;
    }
    const match = findAttributeInData(questionAttributeId);
    if (match) {
      setSelectedAttributeForQuestions(match);
      setIsQuestionDrawerOpen(true);
    }
  }, [questionAttributeId, findAttributeInData, isQuestionDrawerOpen, onQuestionDrawerChange]);

  useEffect(() => {
    if (!isQuestionDrawerOpen || !selectedAttributeForQuestions) {
      return;
    }
    const match = findAttributeInData(selectedAttributeForQuestions.id);
    if (match && match !== selectedAttributeForQuestions) {
      setSelectedAttributeForQuestions(match);
    }
  }, [isQuestionDrawerOpen, selectedAttributeForQuestions, findAttributeInData]);

  const getAllTools = () => {
    ToolService.getAllTools()
      .then((response) => {
        setTools(response.data);
      })
      .catch((error) => {
        console.error('Error fetching tools:', error);
      });
  };

  const getAllOfferings = () => {
    OfferingService.getAllOfferings()
      .then((response) => {
        setOfferings(response.data);
      })
      .catch((error) => {
        console.error('Error fetching offerings:', error);
      });
  };

  const getAllGroups = () => {
    listGroupsAll()
      .then((response) => {
        setGroups(response || []);
      })
      .catch((error) => {
        console.error('Error fetching groups:', error);
      });
  };

  const resolveToolName = useCallback(
    (attribute: any) => {
      const toolId = attribute?.toolId ?? attribute?.tool?.id;
      const inlineName = attribute?.tool?.name ?? attribute?.toolName;
      if (inlineName) return inlineName;
      const match = tools.find((tool: any) => tool?.id === toolId);
      return match?.name ?? '';
    },
    [tools],
  );

  const resolveOfferingName = useCallback(
    (attribute: any) => {
      const offeringId = attribute?.offering?.id ?? attribute?.offeringId;
      const inlineName = attribute?.offering?.name ?? attribute?.offeringName;
      if (inlineName) return inlineName;
      const match = offerings.find((offering: any) => offering?.id === offeringId);
      return match?.name ?? '';
    },
    [offerings],
  );

  const resolveGroupName = useCallback(
    (attribute: any) => {
      const groupId = attribute?.group?.id ?? attribute?.groupId;
      const inlineName = attribute?.group?.name ?? attribute?.groupName;
      if (inlineName) return inlineName;
      const match = groups.find((group: any) => group?.id === groupId);
      return match?.name ?? '';
    },
    [groups],
  );

  const resolveUnitLabel = useCallback((unit?: string | null) => {
    if (!unit) return '';
    return PARAMETER_UNIT_LABELS[unit] ?? unit;
  }, []);

  const resolvePeriodLabel = useCallback((period?: number | null) => {
    if (period === null || period === undefined) return '';
    return PARAMETER_PERIOD_LABELS[String(period)] ?? `${period} Ay`;
  }, []);

  const sortedData = data
    .slice()
    .sort((a, b) => {
      if (a.orderNo == null && b.orderNo == null) {
        return 0;
      } else if (a.orderNo == null) {
        return 1;
      } else if (b.orderNo == null) {
        return -1;
      } else {
        return a.orderNo - b.orderNo;
      }
    });

  const filteredData = sortedData.filter((item) => {
    const searchLower = searchText.toLocaleLowerCase('tr');
    const matchesSearch =
      (item.name?.toLocaleLowerCase('tr').includes(searchLower) || '') ||
      (item.description?.toLocaleLowerCase('tr').includes(searchLower) || '');
    const matchesActiveStatus = filters.showInactive ? true : item.isActive;
    const itemOfferingId = item.offering?.id ?? item.offeringId ?? null;
    const itemGroupId = item.group?.id ?? item.groupId ?? null;
    const matchesOffering =
      filters.offeringId !== null ? itemOfferingId === filters.offeringId : true;
    const matchesGroup =
      filters.groupId !== null ? itemGroupId === filters.groupId : true;
    const matchesCountable =
      filters.countableFilter === 'all'
        ? true
        : filters.countableFilter === 'countable'
          ? item.countable === true
          : item.countable === false;
    const matchesAccreditationRequired = matchesBooleanParameterFilter(
      item.accreditationRequired,
      filters.accreditationRequiredFilter,
    );
    const matchesAccredited = matchesBooleanParameterFilter(
      item.accredited,
      filters.accreditedFilter,
    );
    const matchesUnit = filters.unitFilter ? item.unit === filters.unitFilter : true;

    return (
      matchesSearch &&
      matchesActiveStatus &&
      matchesOffering &&
      matchesGroup &&
      matchesCountable &&
      matchesAccreditationRequired &&
      matchesAccredited &&
      matchesUnit
    );
  });

  // Group data by 'toolId' for display purposes
  const groupData = () => {
    const grouped: any[] = [];
    const toolIdMap = new Map();

    filteredData.forEach((item) => {
      item.key = item.id; // Assign unique keys to items

      if (!item.toolId) {
        // Items without a group
        grouped.push({ ...item, isGroup: false });
      } else {
        // Grouped items
        const tool = tools.find((t: any) => t.id === item.toolId);
        const toolName = tool ? tool.name : 'Unknown Component';
        const toolDescription = tool ? tool.description : '';
        let group = toolIdMap.get(item.toolId);

        if (!group) {
          group = {
            key: 'group-' + item.toolId,
            name: toolName,
            description: toolDescription, // Added the tool's description
            isGroup: true,
            children: [],
          };
          toolIdMap.set(item.toolId, group);
          grouped.push(group);
        }

        group.children.push({ ...item, isGroup: false });
      }
    });

    return grouped;
  };

  const treeData = groupData();

  const handleAdd = () => {
    setSelectedParameter(null);
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setSelectedParameter(record);
    setModalVisible(true);
  };

  const handleSave = (data: any, mode: any) => {
    if (mode === 'edit') {
      AttributeService.update(data.id, data)
        .then(() => {
          setData((prevData) =>
            prevData.map((item) => (item.id === data.id ? data : item))
          );
          openNotification('success', 'Başarılı', 'Parametre başarıyla güncellendi.');
          getAllAttributes();
        })
        .catch((error) => console.error('Update error:', error));
    } else {
      AttributeService.create(data)
        .then((response) => {
          setData((prevData) => [...prevData, response.data]);
          openNotification('success', 'Başarılı', 'Parametre başarıyla eklendi.');
          getAllAttributes();
        })
        .catch((error) => console.error('Add error:', error));
    }
    setModalVisible(false);
  };

  const handleDelete = (id: number) => {
    AttributeService.remove(id)
      .then(() => {
        setData((prevData) => prevData.filter((item: any) => item.id !== id));
        openNotification('success', 'Başarılı', 'Parametre başarıyla silindi.');
      })
      .catch((error) => {
        console.error('Delete error:', error);
        openNotification('error', 'Hata', 'Parametre silinemedi.');
      });
  };

  const handleToggleActive = (record: any) => {
    const updatedRecord = { ...record, isActive: !record.isActive };
    AttributeService.update(updatedRecord.id, updatedRecord)
      .then(() => {
        setData((prevData) =>
          prevData.map((item) =>
            item.id === updatedRecord.id ? updatedRecord : item
          )
        );
        openNotification(
          'success',
          'Başarılı',
          `Parametre başarıyla ${!updatedRecord.isActive ? 'aktif' : 'pasif'
          } yapıldı.`
        );
      })
      .catch((error) => {
        console.error('Error toggling active state:', error);
        openNotification('error', 'Hata', 'Parametre güncellenemedi.');
      });
  };

  const handleOpenPreparationDrawer = (attribute: any) => {
    if (!attribute || attribute.isGroup || !attribute.id) {
      return;
    }
    setPreparationDrawerAttributeId(attribute.id);
    setPreparationDrawerKey((prev) => prev + 1);
  };

  const menu = (record: any) => (
    <Menu>
      <Menu.Item key={`report-manage-${record.id}`}>
        <Link to={`/report-formats/${record.id}`}>Rapor Yönetimi</Link>
      </Menu.Item>
      <Menu.Item
        key={`editCriteria-${record.id}`}
        onClick={() => handleOpenDiscountModal(record.id)}
        disabled={record.isGroup}
      >
        Hesap Kriteri Düzenle
      </Menu.Item>

      <Menu.Item key={`edit-${record.id}`} onClick={() => handleEdit(record)}>
        Düzenle
      </Menu.Item>

      {!record.toolId && (
        <Menu.Item
          key={`preparation-${record.id}`}
          onClick={() => handleOpenPreparationDrawer(record)}
        >
          Ön Hazırlık Metni
        </Menu.Item>
      )}

      <Menu.Item
        key={`questions-${record.id}`}
        onClick={() => handleOpenQuestions(record)}
        disabled={record.isGroup}
      >
        Sorular
      </Menu.Item>

      {/* Aktif/Pasif yapma işlemi */}
      <Menu.Item key={`toggleActive-${record.id}`}>
        <Popconfirm placement="left"
          title={`Bu Parametre'yi ${record.isActive ? 'pasif' : 'aktif'
            } yapmak istediğinize emin misiniz?`}
          onConfirm={() => handleToggleActive(record)}
          okText="Evet"
          cancelText="Hayır"
        /* placement değiştirmek, menüye çakışma durumunu da azaltır */
        >
          <a>{record.isActive ? 'Pasif Yap' : 'Aktif Yap'}</a>
        </Popconfirm>
      </Menu.Item>

      {/* Silme işlemi */}
      <Menu.Item key={`delete-${record.id}`}>
        <Popconfirm placement="left"
          title="Bu Parametre'yi silmek istediğinize emin misiniz?"
          onConfirm={() => handleDelete(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <a>Sil</a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const columns: any = [
    {
      key: 'sort',
      align: 'center',
      width: 80,
      render: (_: any, record: any) => {
        const indentSize = 70;
        const paddingLeft = !record.toolId ? 0 : indentSize;
        let test = record.isGroup ? 0 : indentSize;;
        return (
          <div style={{
            width: `${test ? 0 : 0}px`,
            paddingLeft: `${test ? paddingLeft : 0}px`,
          }}>
            <DragHandle />
          </div>
        );
      },
    },
    {
      title: 'Adı',
      dataIndex: 'name',
      key: 'name',
      width: 200,

      render: (text: any, record: any) => {
        return (
          <>
            {record.isGroup ? (
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{text}</span>
            ) : (
              text
            )}
          </>
        );
      },
    },
    // {
    //   title: 'Açıklama',
    //   dataIndex: 'description',
    //   key: 'description',
    //   render: (text: any, record: any) => {
    //     if (record.isGroup) {
    //       return record.description || null; // Display the group's description
    //     }
    //     return text;
    //   },
    // },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: any) =>
        record.isGroup ? null : `${formatToTL(price)}`,
    },
    {
      title: 'Birim',
      dataIndex: 'unit',
      key: 'unit',
      width: 140,
      render: (unit: string, record: any) =>
        record.isGroup ? null : PARAMETER_UNIT_LABELS[unit ?? ''] ?? unit ?? '-',
    },
    {
      title: 'Azami Süre',
      dataIndex: 'period',
      key: 'period',
      width: 110,
      render: (period: number, record: any) =>
        record.isGroup
          ? null
          : PARAMETER_PERIOD_LABELS[String(period ?? 1)] ?? `${period ?? 1} Ay`,
    },
    {
      title: 'Hizmet Grubu',
      dataIndex: 'offering',
      key: 'offering',
      width: 170,
      render: (offeringId: number | null, record: any) =>
        record?.offering?.name,
    },
    {
      title: 'Kategori',
      dataIndex: 'group',
      key: 'group',
      width: 200,
      render: (_: any, record: any) => record?.group?.name || record?.groupName || '',
    },
    {
      title: 'Sayılabilir',
      dataIndex: 'countable',
      key: 'countable',
      render: (value: boolean, record: any) =>
        record.isGroup ? null : (
          <Tag color={value ? 'blue' : 'default'}>
            {value ? 'Evet' : 'Hayır'}
          </Tag>
        ),
    },
    {
      title: 'Akreditasyon zorunlu mu?',
      dataIndex: 'accreditationRequired',
      key: 'accreditationRequired',
      width: 190,
      render: (value: boolean, record: any) =>
        record.isGroup ? null : (
          <Tag color={value ? 'blue' : 'default'}>{getBooleanParameterLabel(value)}</Tag>
        ),
    },
    {
      title: 'Akredite miyiz?',
      dataIndex: 'accredited',
      key: 'accredited',
      width: 220,
      render: (value: boolean, record: any) =>
        record.isGroup ? null : (
          <Tag color={value ? 'blue' : 'default'}>{getBooleanParameterLabel(value)}</Tag>
        ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: any) =>
        record.isGroup ? null : (
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? 'Aktif' : 'Pasif'}
          </Tag>
        ),
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: any, record: any) =>
        record.isGroup ? null : (
          <Dropdown overlay={menu(record)} key={record.id} trigger={['click']}>
            <Button className="action-button width-auto" type="default">
              İşlemler
            </Button>
          </Dropdown>
        ),
    },
  ];

  const handleExportExcel = useCallback(() => {
    try {
      setExporting(true);
      const headers = [
        'Parametre Adı',
        'Açıklama',
        'Bileşen',
        'Hizmet Grubu',
        'Kategori',
        'Azami Süre',
        'Birim',
        'Fiyat',
        'Saha Kontrol Dk',
        'Rapor Hazırlık Dk',
        'Sayılabilir',
        'Akreditasyon zorunlu mu?',
        'Akredite miyiz?',
        'Birincil',
        'Durum',
      ];
      const rows = filteredData
        .filter((item) => !item.isGroup)
        .map((item) => [
          item?.name ?? '',
          item?.description ?? '',
          resolveToolName(item),
          resolveOfferingName(item),
          resolveGroupName(item),
          resolvePeriodLabel(item?.period),
          resolveUnitLabel(item?.unit),
          item?.price ?? '',
          item?.fieldControlTime ?? '',
          item?.reportPreparationTime ?? '',
          getBooleanParameterLabel(item?.countable),
          getBooleanParameterLabel(item?.accreditationRequired),
          getBooleanParameterLabel(item?.accredited),
          item?.isDefault ? 'Evet' : 'Hayır',
          item?.isActive ? 'Aktif' : 'Pasif',
        ]);
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Parametreler');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'parametreler.xlsx');
    } catch (error) {
      console.error('Parameter excel export error:', error);
      openNotification('error', 'Hata', 'Excel dışa aktarılırken bir hata oluştu.');
    } finally {
      setExporting(false);
    }
  }, [filteredData, resolveGroupName, resolveOfferingName, resolvePeriodLabel, resolveToolName, resolveUnitLabel]);

  // Flatten the treeData for sorting purposes
  const flattenTreeData = (tree: any[]) => {
    const flatData: any[] = [];
    tree.forEach((item) => {
      flatData.push(item);
      if (item.children && item.children.length > 0) {
        item.children.forEach((child: any) => {
          flatData.push(child);
        });
      }
    });
    return flatData;
  };

  const flattenedData = flattenTreeData(treeData);

  const getParentKey = (itemKey: string) => {
    for (const group of treeData) {
      if (
        group.children &&
        group.children.some((child: any) => child.key === itemKey)
      ) {
        return group.key;
      }
    }
    return null;
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) return;

    if (active.id !== over.id) {
      const activeItem = flattenedData.find((item) => item.key === active.id);
      const overItem = flattenedData.find((item) => item.key === over.id);

      if (activeItem && overItem) {
        const activeParentKey = getParentKey(activeItem.key);
        const overParentKey = getParentKey(overItem.key);

        if (activeParentKey === overParentKey) {
          if (activeParentKey) {
            // Reorder within the same group
            const group = treeData.find((item) => item.key === activeParentKey);
            if (group && group.children) {
              const activeIndex = group.children.findIndex(
                (item: any) => item.key === active.id
              );
              const overIndex = group.children.findIndex(
                (item: any) => item.key === over.id
              );
              if (activeIndex !== -1 && overIndex !== -1) {
                const newChildren = arrayMove(group.children, activeIndex, overIndex);
                group.children = newChildren;

                // Update orderNo values
                const updatedItems: any[] = [];
                let orderNo = 1;
                treeData.forEach((item) => {
                  if (item.isGroup && item.children) {
                    item.children.forEach((child: any) => {
                      child.orderNo = orderNo++;
                      updatedItems.push(child);
                    });
                  } else if (!item.isGroup) {
                    item.orderNo = orderNo++;
                    updatedItems.push(item);
                  }
                });

                // Update data state
                setData((prevData) =>
                  prevData.map((item) => {
                    const updatedItem = updatedItems.find(
                      (uItem) => uItem.id === item.id
                    );
                    return updatedItem
                      ? { ...item, orderNo: updatedItem.orderNo }
                      : item;
                  })
                );

                // Send updated order to server
                saveOrderChanges(updatedItems);
              }
            }
          } else {
            // Reorder standalone items
            const activeIndex = treeData.findIndex((item) => item.key === active.id);
            const overIndex = treeData.findIndex((item) => item.key === over.id);

            if (activeIndex !== -1 && overIndex !== -1) {
              const newTreeData = arrayMove(treeData, activeIndex, overIndex);

              // Update orderNo values
              const updatedItems: any[] = [];
              let orderNo = 1;
              newTreeData.forEach((item) => {
                if (item.isGroup && item.children) {
                  item.children.forEach((child: any) => {
                    child.orderNo = orderNo++;
                    updatedItems.push(child);
                  });
                } else if (!item.isGroup) {
                  item.orderNo = orderNo++;
                  updatedItems.push(item);
                }
              });

              // Update data state
              setData((prevData) =>
                prevData.map((item) => {
                  const updatedItem = updatedItems.find(
                    (uItem) => uItem.id === item.id
                  );
                  return updatedItem
                    ? { ...item, orderNo: updatedItem.orderNo }
                    : item;
                })
              );

              // Send updated order to server
              saveOrderChanges(updatedItems);
            }
          }
        }
      }
    }
  };

  const saveOrderChanges = (updatedItems: DataType[]) => {
    const orderUpdates = updatedItems.map((item) => ({
      id: item.id,
      orderNo: item.orderNo,
    }));

    AttributeService.updateOrder(orderUpdates)
      .then(() => {
        openNotification('success', 'Başarılı', 'Sıralama başarıyla güncellendi.');
        getAllAttributes();
      })
      .catch((error: any) => {
        console.error('Error updating order:', error);
        openNotification('error', 'Hata', 'Sıralama güncellenirken hata oluştu.');
      });
  };

  const RowComponent = (props: any) => {
    const { children, ...restProps } = props;
    const record = children[0]?.props?.record || {};
    const key = record.key || restProps['data-row-key'] || 'no-key';

    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: key,
    });

    const style: React.CSSProperties = {
      ...restProps.style,
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    const contextValue = useMemo(
      () => ({
        setActivatorNodeRef,
        listeners,
      }),
      [setActivatorNodeRef, listeners]
    );

    return (
      <RowContext.Provider value={contextValue}>
        <tr {...restProps} ref={setNodeRef} style={style} {...attributes}>
          {children}
        </tr>
      </RowContext.Provider>
    );
  };

  const sortableItems = useMemo(() => {
    return flattenedData.map((item) => item.key);
  }, [flattenedData]);

  return (
    <>
      <Card className="br-30px mb-60px">
        <Row justify={'space-between'} align={'middle'} className='mb-16px'>
          <div>
            <Title style={{ margin: 0, display: 'inline-block' }} level={4}>Parametre Listesi</Title>
            <Typography.Text type='secondary' className='ms-5'>
              <strong>({filteredData.length})</strong> kayıt listelendi
            </Typography.Text>
          </div>
          <Row align={'middle'}>
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-form d-input-filter ml-16px"
              style={{ width: 200 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Tooltip title="Dışa Aktar">
              <Button
                shape='circle'
                className='excel-icon-button'
                onClick={handleExportExcel}
                aria-label='Excel indir'
              >
                <FileExcelOutlined />
              </Button>
            </Tooltip>
            <Tooltip title="Rapor Format Durumu">
              <Link to="/settings/parameters/reports" className="ml-10px">
                <Button
                  shape='circle'
                  className='icon-button'
                  aria-label='Rapor format durumu'
                >
                  <FileTextOutlined />
                </Button>
              </Link>
            </Tooltip>
            <Button
              type="primary"
              className="d-button width-auto ml-10px dark-button"
              onClick={() => setFilterDrawerVisible(true)}
            >
              Filtrele
            </Button>
            <Button
              onClick={handleAdd}
              type="primary"
              className="d-button width-auto ml-10px dark-button"
            >
              Yeni Parametre Ekle
            </Button>
          </Row>
        </Row>

        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          onDragStart={({ active }: any) => setActiveId(active.id)}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            <Table
              scroll={{ x: 768 }}
              columns={columns}
              dataSource={treeData}
              pagination={false}
              rowKey="key"
              components={{
                body: {
                  row: RowComponent,
                },
              }}
              expandable={{
                defaultExpandAllRows: true,
                expandRowByClick: true,
                rowExpandable: (record) => record.isGroup,
              }}
            />
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                }}
              >
                {flattenedData.find((item) => item.key === activeId)?.name}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
        <Drawer
          open={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          destroyOnClose
          width={450}
          title="Filtre"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Typography.Text strong>Hizmet Grubu</Typography.Text>
              <Select
                allowClear
                placeholder="Hizmet grubu seçiniz"
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.offeringId ?? undefined}
                onChange={(value) =>
                  setDrawerFilters((prev) => ({ ...prev, offeringId: value ?? null }))
                }
              >
                {offerings.map((offering) => (
                  <Select.Option key={offering.id} value={offering.id}>
                    {offering.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <Typography.Text strong>Kategori</Typography.Text>
              <Select
                allowClear
                placeholder="Kategori seçiniz"
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.groupId ?? undefined}
                onChange={(value) =>
                  setDrawerFilters((prev) => ({ ...prev, groupId: value ?? null }))
                }
              >
                {groups.map((group: any) => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <Typography.Text strong>Pasifleri göster</Typography.Text>
              <Switch
                checked={drawerFilters.showInactive}
                onChange={(checked) =>
                  setDrawerFilters((prev) => ({ ...prev, showInactive: checked }))
                }
              />
            </div>
            <div>
              <Typography.Text strong>Sayılabilir</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.countableFilter}
                onChange={(value: 'all' | 'countable' | 'uncountable') =>
                  setDrawerFilters((prev) => ({ ...prev, countableFilter: value }))
                }
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'countable', label: 'Evet' },
                  { value: 'uncountable', label: 'Hayır' },
                ]}
              />
            </div>
            <div>
              <Typography.Text strong>Akreditasyon zorunlu mu?</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.accreditationRequiredFilter}
                onChange={(value: BooleanParameterFilter) =>
                  setDrawerFilters((prev) => ({ ...prev, accreditationRequiredFilter: value }))
                }
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'yes', label: 'Evet' },
                  { value: 'no', label: 'Hayır' },
                ]}
              />
            </div>
            <div>
              <Typography.Text strong>Akredite miyiz?</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.accreditedFilter}
                onChange={(value: BooleanParameterFilter) =>
                  setDrawerFilters((prev) => ({ ...prev, accreditedFilter: value }))
                }
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'yes', label: 'Evet' },
                  { value: 'no', label: 'Hayır' },
                ]}
              />
            </div>
            <div>
              <Typography.Text strong>Birim</Typography.Text>
              <Select
                allowClear
                placeholder="Birim seçiniz"
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.unitFilter ?? undefined}
                onChange={(value) =>
                  setDrawerFilters((prev) => ({ ...prev, unitFilter: value ?? null }))
                }
                options={PARAMETER_UNIT_OPTIONS}
              />
            </div>
            <div>
              <Typography.Text strong>Rapor Durumu</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.reportFormatsFilter}
                onChange={(value: 'all' | 'with' | 'without') =>
                  setDrawerFilters((prev) => ({ ...prev, reportFormatsFilter: value }))
                }
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'with', label: 'Olanlar' },
                  { value: 'without', label: 'Olmayanlar' },
                ]}
              />
            </div>
            <div>
              <Typography.Text strong>Ön Hazırlık Metni</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={drawerFilters.preparationTextFilter}
                onChange={(value: 'all' | 'with' | 'without') =>
                  setDrawerFilters((prev) => ({ ...prev, preparationTextFilter: value }))
                }
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'with', label: 'Olanlar' },
                  { value: 'without', label: 'Olmayanlar' },
                ]}
              />
            </div>
          </Space>
          <Row justify="end" gutter={8} style={{ marginTop: 24 }}>
            <Col>
              <Button
                onClick={() => {
                  const defaults = createDefaultFilters();
                  setDrawerFilters(defaults);
                  setFilters({ ...defaults });
                }}
              >
                Temizle
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={() => {
                  setFilters({ ...drawerFilters });
                  setFilterDrawerVisible(false);
                }}
              >
                Filtrele
              </Button>
            </Col>
          </Row>
        </Drawer>
        <AttributePreparationDrawer
          key={preparationDrawerKey}
          attributeId={preparationDrawerAttributeId}
        />
        <ParameterModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSave={handleSave}
          initialData={selectedParameter}
        />
        <Drawer
          open={isQuestionDrawerOpen}
          onClose={handleCloseQuestions}
          destroyOnClose
          width={960}
          title={selectedAttributeForQuestions ? `${selectedAttributeForQuestions?.name ?? 'Parametre'} Soruları` : 'Sorular'}
        >
          {selectedAttributeForQuestions ? (
            <QuestionList key={selectedAttributeForQuestions.id} attributeId={selectedAttributeForQuestions.id} />
          ) : null}
        </Drawer>
      </Card>
      {selectedAttributeId !== null && (
        <DiscountCriteriaModal
          visible={discountModalVisible}
          onClose={() => setDiscountModalVisible(false)}
          attributeId={selectedAttributeId}
        />
      )}
    </>
  );
};

export default ParameterList;
